import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import type { TableProps } from 'antd';
import { Button, Table, Tooltip, Typography } from 'antd';

import MyIcon from 'components/Icon';
import { PRODUCT_STATUS_LABEL } from 'helpers/enums';
import type { RenderExpandIconProps } from 'rc-table/lib/interface';
import ProductStatusTag from 'scenes/Producer/Product/MyProducts/components/ProductStatusTag';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';

import { Icon } from '@eduzz/ui-antd-theme';

import DataChildren from '../DataChildren';
import IrregularDataChildren from '../Irregular/components/DataChildren';
import ShowPrice from '../ShowPrice';

type TableSort = { field: string; direction: 'asc' | 'desc' };

enum AppCodes {
  BLINKET = 28,
  ALUMY = 36
}

export type PaginationType = {
  current_page: number;
  per_page: number;
  optionsPerPage: string;
  onChangePage: any;
  onChangePerPage: any;
  total: number;
};

type Props = {
  products: any[];
  listProductsChildren?: any;
  order: string;
  coproduction?: boolean;
  irregular?: boolean;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onClickOptions?: (product: any, activatedChildren: any) => void;
  isLoading: boolean;
  pagination: PaginationType;
};

const ShowApp = ({ product }: any) => {
  const apps = {
    blinket: {
      logo: '/img/eduzz-eventos-symbol.svg',
      logoClassName: 'w-25',
      description: 'Lá você vai encontrar tudo para criar e gerenciar seus eventos.',
      whyLink: '',
      whyLabel: 'o Eduzz Eventos',
      tooltip: 'Esse produto foi cadastrado na plataforma Eduzz Eventos',
      alt: 'Eduzz Eventos'
    },
    alumy: {
      logo: '/img/alumy-symbol.svg',
      logoClassName: 'w-5',
      description: 'Lá você vai encontrar tudo para criar e gerenciar seus clubes de assinatura.',
      whyLink: '',
      whyLabel: 'a Alumy',
      tooltip: 'Esse produto foi cadastrado na plataforma Alumy',
      alt: 'Alumy'
    }
  };

  const verifyApp = useCallback(() => {
    if (product.cnt_partner_cod === AppCodes.BLINKET) return apps.blinket;
    if (product.cnt_partner_cod === AppCodes.ALUMY) return apps.alumy;
    return null;
  }, [apps.alumy, apps.blinket, product]);

  const currentApp = verifyApp();

  return (
    <div>
      {currentApp && (
        <Tooltip title={currentApp.tooltip}>
          <div className={`${currentApp.logoClassName} shrink-0`}>
            <img src={currentApp.logo} alt={currentApp.alt} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const ProductTable = ({
  products,
  order,
  listProductsChildren,
  coproduction,
  irregular,
  onSort,
  onClickOptions,
  isLoading,
  pagination
}: Props) => {
  const [field, direction] = order.split('-');
  const [sort, setSort] = useState<TableSort>({ field, direction } as TableSort);

  const history = useHistory();

  const onSortTable = useCallback(
    (sortField: string, sortDirection: 'asc' | 'desc') => {
      setSort({
        field: sortField,
        direction: sortDirection
      });
      onSort(sortField, sortDirection);
    },
    [onSort]
  );

  const handleSortClick = useCallback(
    (sortField: string) => {
      const isAsc = sort?.field === sortField && sort?.direction === 'asc';
      onSortTable(sortField, isAsc ? 'desc' : 'asc');
    },
    [sort?.direction, sort?.field, onSortTable]
  );

  const onDoubleClick = (id: string, isHighticket: boolean) => {
    if (isHighticket) return;
    if (coproduction) return;
    history.push(`/producer/product/editproduct/${id}`);
  };

  const parseOptionsPerPage = (options: string) => options.split(',').map(Number);

  const dataSource = useMemo(() => products ?? [], [products]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    setExpandedRowKeys([]);
  }, [dataSource]);

  const columns = useMemo((): TableProps['columns'] => {
    const mdUp: ('md' | 'lg' | 'xl' | 'xxl')[] = ['md', 'lg', 'xl', 'xxl'];

    if (irregular) {
      return [
        {
          key: 'product',
          title: 'Produto',
          render: (_: unknown, row: any) => (
            <div className='flex min-w-0 items-center gap-3'>
              <div className='border-outline-default bg-surface-subtle relative size-10 shrink-0 overflow-hidden rounded-sm border'>
                {row.image ? <img src={row.image} alt='' className='absolute inset-0 size-full object-cover' /> : null}
              </div>
              <div className='min-w-0 flex-1 overflow-hidden'>
                <div className='flex min-w-0 items-center gap-1.5'>
                  <Typography.Text
                    strong
                    ellipsis={{ tooltip: row.title || 'Desconhecido' }}
                    className='min-w-0 flex-1'
                  >
                    {row.title || 'Desconhecido'}
                  </Typography.Text>
                  <ShowApp product={row} />
                  {row.charge_type === 'A' && (
                    <Tooltip title='Recorrência' placement='top'>
                      <div className='w-3.75 shrink-0'>
                        <MyIcon className='icon' name='icon-assinaturas' />
                      </div>
                    </Tooltip>
                  )}
                </div>
                <Typography.Text type='secondary'>{row.id}</Typography.Text>
                <div className='mt-1 flex flex-wrap items-center gap-2 md:hidden'>
                  {!row.has_children && <ProductStatusTag status={row.moderation_status} productType={row.type} />}
                </div>
              </div>
            </div>
          )
        },
        {
          key: 'moderation',
          title: 'Moderação',
          align: 'left',
          width: 144,
          responsive: mdUp,
          render: (_: unknown, row: any) =>
            !row.has_children ? <ProductStatusTag status={row.moderation_status} productType={row.type} /> : null
        },
        {
          key: 'actions',
          title: 'Ações',
          width: 48,
          align: 'right',
          render: (_: unknown, row: any) =>
            !!row?.pending_actions ? (
              <TableActionButton
                onClick={() => onClickOptions(row, row.has_children)}
                disabled={!!listProductsChildren?.isFetching}
              />
            ) : null,
          onCell: (_record: any, index?: number) => ({
            id: `product-see-details-${index ?? 0}`
          })
        },
        Table.EXPAND_COLUMN
      ];
    }

    if (coproduction) {
      return [
        {
          key: 'product',
          title: 'Produto',
          render: (_: unknown, row: any) => (
            <div className='flex min-w-0 items-center gap-3'>
              <div className='border-outline-default bg-surface-subtle relative size-10 shrink-0 overflow-hidden rounded-sm border'>
                {row.image ? <img src={row.image} alt='' className='absolute inset-0 size-full object-cover' /> : null}
              </div>
              <div className='min-w-0 flex-1 overflow-hidden'>
                <div className='flex min-w-0 items-center gap-1.5'>
                  <Typography.Text
                    strong
                    ellipsis={{ tooltip: row.title || 'Desconhecido' }}
                    className='min-w-0 flex-1'
                  >
                    {row.title || 'Desconhecido'}
                  </Typography.Text>
                  <ShowApp product={row} />
                  {!row.has_children && row.charge_type === 'A' && (
                    <Tooltip title='Recorrência' placement='top'>
                      <div className='w-3.75 shrink-0'>
                        <MyIcon className='icon' name='icon-assinaturas' />
                      </div>
                    </Tooltip>
                  )}
                </div>
                <Typography.Text type='secondary'>{row.id}</Typography.Text>
                <div className='mt-1 flex flex-wrap items-center gap-2 md:hidden'>
                  {!row.has_children && <ProductStatusTag status={row.moderation_status} productType={row.type} />}
                  <Typography.Text>{PRODUCT_STATUS_LABEL[row.status]}</Typography.Text>
                  <ShowPrice product={row} commission='coproduction' />
                </div>
              </div>
            </div>
          )
        },
        {
          key: 'moderation',
          title: 'Moderação',
          align: 'left',
          width: 144,
          responsive: mdUp,
          render: (_: unknown, row: any) =>
            !row.has_children ? <ProductStatusTag status={row.moderation_status} productType={row.type} /> : null
        },
        {
          key: 'status',
          title: 'Status',
          align: 'left',
          width: 112,
          responsive: mdUp,
          render: (_: unknown, row: any) => <Typography.Text>{PRODUCT_STATUS_LABEL[row.status]}</Typography.Text>
        },
        {
          key: 'price',
          title: 'Preço',
          align: 'left',
          width: 176,
          responsive: mdUp,
          render: (_: unknown, row: any) => <ShowPrice product={row} commission='coproduction' />
        },
        {
          key: 'commission',
          title: 'Comissão',
          align: 'left',
          width: 112,
          responsive: mdUp,
          render: (_: unknown, row: any) => <ShowPrice product={row} commission='percentage' />
        },
        {
          key: 'commission_subscription',
          title: 'Comissão recorrente',
          align: 'left',
          width: 160,
          responsive: mdUp,
          render: (_: unknown, row: any) => <ShowPrice product={row} commission='subscription' />
        },
        {
          key: 'actions',
          title: 'Ações',
          width: 48,
          align: 'right',
          fixed: 'right',
          render: (_: unknown, row: any) => (
            <TableActionButton
              onClick={() => onClickOptions(row, row.has_children)}
              disabled={!!listProductsChildren?.isFetching}
            />
          ),
          onCell: (_record: any, index?: number) => ({
            id: `product-see-details-${index ?? 0}`
          })
        },
        Table.EXPAND_COLUMN
      ];
    }

    return [
      {
        key: 'product',
        title: (
          <Button
            id='my-products-sort-title'
            type='text'
            size='small'
            className='h-auto p-0'
            onClick={() => handleSortClick('title')}
          >
            <strong>Produto {sort?.field === 'title' ? (sort?.direction === 'asc' ? '↑' : '↓') : ''}</strong>
          </Button>
        ),
        render: (_: unknown, row: any) => (
          <div className='flex min-w-0 items-center gap-3'>
            <div className='border-outline-default bg-surface-subtle relative size-10 shrink-0 overflow-hidden rounded-sm border'>
              {row.image ? <img src={row.image} alt='' className='absolute inset-0 size-full object-cover' /> : null}
            </div>
            <div className='min-w-0 flex-1 overflow-hidden'>
              <div className='flex min-w-0 items-center gap-1.5'>
                <Typography.Text strong ellipsis={{ tooltip: row.title || 'Desconhecido' }}>
                  {row.title || 'Desconhecido'}
                </Typography.Text>
                <ShowApp product={row} />
                {!row.has_children && row.charge_type === 'A' && (
                  <Tooltip title='Recorrência' placement='top'>
                    <div className='w-3.75 shrink-0'>
                      <MyIcon className='icon' name='icon-assinaturas' />
                    </div>
                  </Tooltip>
                )}
              </div>
              <Typography.Text type='secondary'>{row.id}</Typography.Text>
              <div className='mt-1 flex flex-wrap items-center gap-2 md:hidden'>
                {!row.has_children && <ProductStatusTag status={row.moderation_status} productType={row.type} />}
                <Typography.Text>{PRODUCT_STATUS_LABEL[row.status]}</Typography.Text>
                <ShowPrice product={row} commission='normal' />
              </div>
            </div>
          </div>
        )
      },
      {
        key: 'moderation',
        title: 'Moderação',
        align: 'left',
        width: 144,
        responsive: mdUp,
        render: (_: unknown, row: any) =>
          !row.has_children ? <ProductStatusTag status={row.moderation_status} productType={row.type} /> : null
      },
      {
        key: 'status',
        title: 'Status',
        align: 'left',
        width: 90,
        responsive: mdUp,
        render: (_: unknown, row: any) => <Typography.Text>{PRODUCT_STATUS_LABEL[row.status]}</Typography.Text>
      },
      {
        key: 'price',
        title: (
          <Button
            id='my-products-sort-price'
            type='text'
            size='small'
            className='h-auto p-0'
            onClick={() => handleSortClick('price')}
          >
            <strong>Preço {sort?.field === 'price' ? (sort?.direction === 'asc' ? '↑' : '↓') : ''}</strong>
          </Button>
        ),
        align: 'left',
        width: 220,
        responsive: mdUp,
        render: (_: unknown, row: any) => <ShowPrice product={row} commission='normal' />
      },
      {
        key: 'actions',
        title: 'Ações',
        width: 60,
        fixed: 'right',
        align: 'right',
        render: (_: unknown, row: any) => (
          <TableActionButton
            onClick={() => onClickOptions(row, row.has_children)}
            disabled={!!listProductsChildren?.isFetching}
          />
        ),
        onCell: (_record: any, index?: number) => ({
          id: `product-see-details-${index ?? 0}`
        })
      },
      Table.EXPAND_COLUMN
    ];
  }, [
    irregular,
    coproduction,
    sort?.field,
    sort?.direction,
    onClickOptions,
    listProductsChildren?.isFetching,
    handleSortClick
  ]);

  const expandable = useMemo(
    () => ({
      expandedRowKeys,
      onExpandedRowsChange: (keys: readonly React.Key[]) => {
        setExpandedRowKeys([...keys]);
      },
      rowExpandable: (record: any) => !!record.has_children,
      expandIcon: ({ expanded, onExpand, record, expandable: canExpand }: RenderExpandIconProps<any>) => {
        if (!canExpand) {
          return null;
        }

        return (
          <Button
            type='text'
            icon={<Icon.ChevronDown className={expanded ? 'rotate-180' : undefined} />}
            onClick={e => {
              onExpand(record, e);
              e.stopPropagation();
            }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Recolher variações' : 'Expandir variações'}
          />
        );
      },
      expandedRowRender: (record: any) =>
        irregular ? (
          <IrregularDataChildren product={record} onClickOptions={onClickOptions} />
        ) : (
          <DataChildren product={record} onClickOptions={onClickOptions} />
        )
    }),
    [expandedRowKeys, irregular, onClickOptions]
  );

  return (
    <Table
      tableLayout='fixed'
      dataSource={dataSource}
      columns={columns}
      rowKey='id'
      loading={isLoading}
      size='small'
      pagination={
        isLoading || !pagination
          ? false
          : {
              total: pagination?.total || 0,
              current: pagination?.current_page || 1,
              pageSize: pagination?.per_page || 10,
              pageSizeOptions: parseOptionsPerPage(pagination?.optionsPerPage || '10,20,50'),
              showSizeChanger: true,
              onChange: page => pagination.onChangePage(page),
              onShowSizeChange: (_: number, size: number) => pagination.onChangePerPage(size)
            }
      }
      expandable={dataSource.some(p => p.has_children) ? expandable : undefined}
      onRow={(record: any) => ({
        onDoubleClick: () => onDoubleClick(record.id, record.is_highticket)
      })}
    />
  );
};

export default memo(ProductTable);
